import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { debounce } from "lodash";
import MainBody from "../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";
import sortCaret from "../../components/SortCaret";
import UploadJob from "./UploadJob";
import AddEditJob from "./AddEditJob";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import JobActionButton from "./JobActionButton";
import Confirmation from "../../components/Confirmation";
import { useTrust } from "../../lib/context/TrustContext";
import ExpandJob from "./ExpandJob";
import { CaretDownIcon, CaretRightIcon } from "@radix-ui/react-icons";

const Jobs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [jobList, setJobsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { trustId } = useTrust();

  const [jobId, setJobId] = useState(null);
  const [showAddEditJob, setShowAddEditJob] = useState(false);
  const [showJobDelete, setShowJobDelete] = useState(false);

  const toggleShowAddEditJob = () => {
    if (showAddEditJob) {
      setEdit(false);
      setJobId(null);
    }
    setShowAddEditJob(!showAddEditJob);
  };

  const toggleShowDelete = () => {
    if (showJobDelete) {
      setJobId(null);
    }
    setShowJobDelete(!showJobDelete);
  };

  const fetchJobs = useCallback(
    async (passLimit, passPage) => {
      if (trustId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.GET_JOBS}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&trust_id=${trustId ? trustId : ""}`
          );

          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const jobList = data.data.data;
          let notSelectedIds = [];
          for (let i = 0; i < jobList?.length; i++) {
            notSelectedIds.push(jobList[i]?.id);
          }
          // setNotSelectedRowIds(notSelectedIds);
          setJobsList(jobList);
          return data.data;
        } catch (error) {
          setLoading(false);
          setJobsList([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, trustId]
  );

  const jobsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: jobList[i]?.id,
        title: jobList[i]?.title ? jobList[i]?.title : "-",
        code: jobList[i]?.code ? jobList[i]?.code : "-",
        description: jobList[i]?.description ? jobList[i]?.description : "-",
        action: (
          <>
            <JobActionButton
              id={jobList[i]?.id}
              setEdit={setEdit}
              setJobId={setJobId}
              used={jobList[i]?.is_used ? true : false}
              toggleShowAddEditJob={toggleShowAddEditJob}
              toggleShowDelete={toggleShowDelete}
            />
          </>
        ),
      });
    }
    return items;
  };

  const jobs = jobsGenerator(jobList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "title",
      text: "Title",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "code",
      text: "Code",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "description",
      text: "Description",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchJobs(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchJobs(limit, page);
  };

  // Single COA Delete
  const handleJobDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.JOB_DELETE}/${jobId}`);
      setLoading(false);
      fetchJobs(limit, page);
    } catch (error) {
      setLoading(false);
    }
  };

  const expandRow = {
    renderer: (row) => (
      <>
        <ExpandJob expandList={true} id={row?.key} />
      </>
    ),
    onlyOneExpanding: true,
    showExpandColumn: true,
    expandByColumnOnly: true,
    headerClasses: THClasses,
    expandColumnRenderer: ({ expanded }) => {
      if (expanded) {
        return <CaretDownIcon />;
      }
      return <CaretRightIcon />;
    },
    className: TDClasses,
    expandHeaderColumnRenderer: () => {
      return <></>;
    },
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        mainTitle={"Jobs/Matters"}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Import Job */}
            <UploadJob
              fetchData={() => fetchJobs(limit, page)}
              trustId={trustId}
              ButtonName={"Import"}
              setLoading={setLoading}
            />

            {/* Add Job */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Job/Matter" : "Add Job/Matter"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setJobId(null);
                setShowAddEditJob(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Job/Matter</span>
                </div>
              }
              className={"sm:max-w-1/2"}
              open={showAddEditJob}
              buttonClassName={""}
              setOpen={setShowAddEditJob}
              write={true}
            >
              <AddEditJob
                edit={edit}
                toggleShowAddEditJob={toggleShowAddEditJob}
                id={jobId}
                trustId={trustId}
                fetchJobData={() => fetchJobs(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={jobs}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              expandRow={expandRow}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Job/Matter"}
        open={showJobDelete}
        setOpen={setShowJobDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete job?"}
        okAction={async () => {
          handleJobDelete();
          toggleShowDelete();
          // fetchJobs();
        }}
        cancelAction={() => {
          setJobId(null);
          setShowJobDelete(!showJobDelete);
        }}
      />
    </>
  );
};

export default Jobs;
